/**
 * The whole application, assembled: cache -> history -> boot gate -> chrome ->
 * one of seven routes.
 *
 * The nesting order is the argument:
 *  - QueryClientProvider outermost, because useLogout() must be able to clear
 *    a cache that outlives every view.
 *  - Router above Boot, so the URL a deep link carried (/tabelle?state=…) is
 *    still there after the login round trip.
 *  - The error boundary OUTSIDE Boot, so a throw from any view -- or from Boot
 *    itself -- still lands on a screen instead of a blank page. Boot's own
 *    failures never reach it: react-query hands them back as `isError` state,
 *    which Boot turns into a named screen before the boundary sees anything.
 *  - Layout inside Boot, because the chrome renders `me` and there is no `me`
 *    before Boot says so.
 */
import { Component, useEffect } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Boot, useAdminConfig, useLogout } from './Boot';
import { Layout } from './Layout';
import { Router, useRoute } from './Router';
import { Blocking } from './components/Blocking';
import { createQueryClient } from './queries';
import { t } from './lib/i18n';
import { pathFor, type RouteId } from './lib/router';
import type { MeResponse } from './lib/types';
import { Start } from './views/Start';
import { Staff } from './views/Staff';
import { Sheet } from './views/Sheet';
import { MasterData } from './views/MasterData';
import { Ebay } from './views/Ebay';
import { Jobs } from './views/Jobs';

// One client for the lifetime of the tab. Created here rather than in a
// useState so a remount (StrictMode, an error boundary reset) reuses the cache
// instead of re-fetching the whole shell.
const queryClient = createQueryClient();

/**
 * A blank page is the one failure mode with no diagnosis at all: no message,
 * no retry, nothing in a screenshot. Anything a render throws lands here and
 * becomes the same named screen the boot failures use.
 */
class ShellErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The console is the only sink this SPA has; there is no client-side error
    // reporting service in this package, and adding one is not a shell decision.
    console.error('[admin] render failed', error, info.componentStack);
  }

  render() {
    if (this.state.failed) {
      // A reload, not a setState: the component tree that threw is not
      // trustworthy, and boot is cheap.
      return <Blocking kind="generic" onRetry={() => window.location.reload()} />;
    }
    return this.props.children;
  }
}

function NotFound() {
  return (
    <section className="not-found">
      <h1>{t('notFound.title')}</h1>
      <p>{t('notFound.body')}</p>
    </section>
  );
}

/**
 * Exported for test/shell.test.ts: a route switch that mounts the wrong view
 * is a silent regression -- every view renders, just not the one the URL
 * names -- and asserting on the returned element's type catches it without a
 * renderer.
 */
export function viewFor(route: RouteId): ReactNode {
  switch (route) {
    case 'start': return <Start />;
    case 'staff': return <Staff />;
    case 'sheet': return <Sheet />;
    case 'masterData': return <MasterData />;
    case 'ebay': return <Ebay />;
    case 'jobs': return <Jobs />;
    // /anmelden with a valid session: Boot already answered the only question
    // this route asks. Showing the Start view is truthful; a login form for a
    // user who is logged in is not. The URL itself is corrected by
    // applyLoginRedirect below, so this branch only ever paints for the one
    // render before that effect runs.
    case 'login': return <Start />;
    case 'notFound': return <NotFound />;
  }
}

/**
 * Task 6 review m-5: without this, a signed-in visit to /anmelden renders
 * Start's content under the wrong URL forever (a refresh keeps it, no nav
 * entry is aria-current). `replace: true` is binding, not a style choice
 * (Task 7 dispatch): a `push` would leave /anmelden as a live history entry,
 * and a user who presses Back would land back on it -- immediately
 * re-triggering this exact redirect, a loop between the app and a login
 * screen for a session that is not actually signed out.
 *
 * Exported and given `navigate` as a parameter, the same shape as
 * Boot.tsx's `performLogout`, so it is pinned in Node (test/shell.test.ts)
 * without a renderer.
 */
export function applyLoginRedirect(
  route: RouteId,
  navigate: (path: string, options?: { replace?: boolean }) => void,
): void {
  if (route === 'login') navigate(pathFor('start'), { replace: true });
}

/**
 * Exported for test/dom/layoutGating.test.tsx (review I-1, fix round 2):
 * whether the redirect effect above is actually wired into a real render is
 * a behavioural question a Node test cannot answer (Shell only runs inside
 * React's own render cycle), so it is asserted there instead, inside the
 * suite that already renders real components — no fourth DOM suite, per the
 * controller's ruling that the cap is on the number of suites, not on the
 * assertions inside one.
 */
export function Shell({ me }: { me: MeResponse }) {
  const { route, hash, navigate } = useRoute();
  const config = useAdminConfig(); // already resolved by Boot; this is a cache read
  const logout = useLogout();
  useEffect(() => applyLoginRedirect(route, navigate), [route, navigate]);
  return (
    <Layout
      me={me}
      buildEnv={config.data?.buildEnv ?? 'unknown'}
      route={route}
      // The rail's sub-entries mark the ACTIVE sub-tab. The fragment travels
      // as-is and is parsed by lib/router.ts's pure parsers inside Layout,
      // never re-derived from window.location in JSX.
      hash={hash}
      onNavigate={navigate}
      onLogout={() => void logout()}
    >
      {viewFor(route)}
    </Layout>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ShellErrorBoundary>
          <Boot>{(me) => <Shell me={me} />}</Boot>
        </ShellErrorBoundary>
      </Router>
    </QueryClientProvider>
  );
}
